import React, { useMemo, useState } from "react";
import { useStepper } from "../ui/stepper";

import { CompanionForm } from "../companionForm/CompanionForm";
import { CompanionFormStepItem } from "../companionForm/CompanionFormStepItem";
import { CompanionFormStep } from "../companionForm/CompanionFormStep";
import { CompanionFormProgress } from "../companionForm/CompanionFormProgress";
import { CompanionFormProgressItem } from "../companionForm/CompanionFormProgressItem";
import { LandingElizaFormIntroHeader } from "./LandingElizaFormIntroHeader";
import {
  GIRL_AGES,
  GIRL_REALISM_BODY_TYPE,
  GIRL_REALISM_EYE_COLOR,
  GIRL_REALISM_HAIR_COLORS,
  GIRL_REALISM_HAIR_LENGTH,
  GIRL_REALISM_HAIRSTYLES,
  GIRL_REALISM_SUGGESTED_NAMES,

  // Landing Form Constants
  ELIZA_FORM_STEPS as STEPS,
  ELIZA_PROGRESS_STEPS,
  ELIZA_STEP_LABELS,
  PROGRESS_STEP_MAPPING,
  GIRL_REALISM_ETHNICITY_GIFS,
  IGNORE_PROGRESS_STEPS,
} from "../../lib/constants";
import { LandingElizaFormSummary } from "./LandingElizaFormSummary";
import { LandingElizaFormCreate } from "./LandingElizaFormCreate";
import { LandingElizaFormAuth } from "./LandingElizaFormAuth";

export const LandingElizaForm = () => {
  const { navigate: navigateStep, currentStep } = useStepper();

  const [data, setData] = useState({
    style: null,
    ethnicity: null,
    body: null,
    eyes: null,
    hairColor: null,
    hairLength: null,
    hairStyle: null,

    soulName: null,
    soulAge: null,
  });

  const showProgress = useMemo(() => {
    if (currentStep === STEPS.ETHNICITY) return false;

    return !IGNORE_PROGRESS_STEPS.includes(currentStep);
  }, [currentStep]);

  const handleChange = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setData((object) => {
      Object.keys(object).forEach((key) => {
        object[key] = null;
      });

      return object;
    });
  };

  return (
    <CompanionForm headerHeight={60}>
      {/* Header */}
      {currentStep === STEPS.ETHNICITY && <LandingElizaFormIntroHeader />}
      {/* Progress - Header items */}
      {showProgress && (
        <CompanionFormProgress>
          {Object.values(ELIZA_PROGRESS_STEPS).map((displayStep, index) => {
            const isActive = PROGRESS_STEP_MAPPING[currentStep] === displayStep;
            return (
              <CompanionFormProgressItem
                key={displayStep}
                pos={index + 1}
                isActive={isActive}
              >
                {ELIZA_STEP_LABELS[displayStep]}
              </CompanionFormProgressItem>
            );
          })}
        </CompanionFormProgress>
      )}
      {/* Realism Ethnicity */}
      <CompanionFormStep
        id={STEPS.ETHNICITY}
        next={!!data.ethnicity && (() => navigateStep(STEPS.BODY))}
        className="max-w-screen-2xl"
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Ethnicity
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {GIRL_REALISM_ETHNICITY_GIFS.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.ethnicity}
              isActive={data.ethnicity === item.id}
              onChange={(value) => handleChange("ethnicity", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Body */}
      <CompanionFormStep
        id={STEPS.BODY}
        next={!!data.body && (() => navigateStep(STEPS.EYES))}
        back={() => navigateStep(STEPS.ETHNICITY)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Body
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_REALISM_BODY_TYPE.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.body}
              isActive={data.body === item.id}
              onChange={(value) => handleChange("body", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Eyes */}
      <CompanionFormStep
        id={STEPS.EYES}
        next={!!data.eyes && (() => navigateStep(STEPS.HAIR_STYLE))}
        back={() => navigateStep(STEPS.BODY)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Eyes
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_REALISM_EYE_COLOR.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.eyes}
              isActive={data.eyes === item.id}
              onChange={(value) => handleChange("eyes", value)}
              imageClassName="w-full h-auto aspect-[16/6]"
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* HAIR STYLE */}
      <CompanionFormStep
        id={STEPS.HAIR_STYLE}
        next={!!data.hairStyle && (() => navigateStep(STEPS.HAIR_LENGTH))}
        back={() => navigateStep(STEPS.EYES)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Style
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {GIRL_REALISM_HAIRSTYLES.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairStyle}
              isActive={data.hairStyle === item.id}
              onChange={(value) => handleChange("hairStyle", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* HAIR LENGTH */}
      <CompanionFormStep
        id={STEPS.HAIR_LENGTH}
        next={!!data.hairLength && (() => navigateStep(STEPS.HAIR_COLOR))}
        back={() => navigateStep(STEPS.HAIR_STYLE)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Length
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {GIRL_REALISM_HAIR_LENGTH.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairLength}
              isActive={data.hairLength === item.id}
              onChange={(value) => handleChange("hairLength", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* HAIR COLOR */}
      <CompanionFormStep
        id={STEPS.HAIR_COLOR}
        next={!!data.hairColor && (() => navigateStep(STEPS.SOUL))}
        back={() => navigateStep(STEPS.HAIR_LENGTH)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Hair Color
        </h2>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {GIRL_REALISM_HAIR_COLORS.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              image={item.image}
              hasSelected={!!data.hairColor}
              isActive={data.hairColor === item.id}
              onChange={(value) => handleChange("hairColor", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Soul */}
      <CompanionFormStep
        id={STEPS.SOUL}
        next={() => navigateStep(STEPS.SUMMARY)}
        back={() => navigateStep(STEPS.HAIR_COLOR)}
      >
        <h2 className="text-xl lg:text-2xl font-bold text-center">
          Choose Soul Name
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_REALISM_SUGGESTED_NAMES.map((item) => (
            <CompanionFormStepItem
              key={item}
              value={item}
              label={item}
              hasSelected={!!data.soulName}
              isActive={data.soulName === item}
              onChange={(value) => handleChange("soulName", value)}
            />
          ))}
        </div>

        <h2 className="text-xl lg:text-2xl font-bold text-center mt-8">
          Choose Soul Age
        </h2>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {GIRL_AGES.map((item) => (
            <CompanionFormStepItem
              key={item.id}
              value={item.id}
              label={item.label}
              hasSelected={!!data.soulAge}
              isActive={data.soulAge === item.id}
              onChange={(value) => handleChange("soulAge", value)}
            />
          ))}
        </div>
      </CompanionFormStep>
      {/* Summary */}
      <LandingElizaFormSummary data={data} />
      {/* Create */}
      <LandingElizaFormCreate data={data} />
      {/* Auth */}
      <LandingElizaFormAuth data={data} />
    </CompanionForm>
  );
};
